import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, {
  formatPrice,
  readSessionValue,
  removeSessionValue,
  writeSessionValue,
} from "../config/siteConfig";
import "../css/cartpage.scss";

function CartPage() {
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [promoCode, setPromoCode] = useState(
    () => readSessionValue(`${siteConfig.brandName}.promo`, "")
  );
  const [promoApplied, setPromoApplied] = useState(
    () => readSessionValue(`${siteConfig.brandName}.promo`) === "COLOR10"
  );
  const [promoMessage, setPromoMessage] = useState("");
  const [giftWrap, setGiftWrap] = useState(false);

  useEffect(() => {
    document.title = `Shopping bag | ${siteConfig.displayName}`;
  }, []);

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const discount = promoApplied ? subtotal * 0.1 : 0;
  const discountedSubtotal = subtotal - discount;
  const shipping =
    discountedSubtotal >= siteConfig.freeShippingThreshold ? 0 : siteConfig.standardShipping;
  const remaining = Math.max(0, siteConfig.freeShippingThreshold - discountedSubtotal);
  const progress = Math.min(100, (discountedSubtotal / siteConfig.freeShippingThreshold) * 100);

  const handlePromo = (event) => {
    event.preventDefault();
    if (promoCode.trim().toUpperCase() === "COLOR10") {
      setPromoApplied(true);
      writeSessionValue(`${siteConfig.brandName}.promo`, "COLOR10");
      setPromoMessage("COLOR10 applied — 10% off.");
    } else {
      setPromoApplied(false);
      removeSessionValue(`${siteConfig.brandName}.promo`);
      setPromoMessage("That code is not available. Try COLOR10 for this demo.");
    }
  };

  if (!detailedItems.length) {
    return (
      <main className="cart-page cart-page--empty">
        <section>
          <span className="cart-empty-mark">0</span>
          <h1>Your cart is empty</h1>
          <p>Fill it with color, confident tailoring and something unexpected.</p>
          <Link className="button" to="/shop">Continue shopping</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-shell">
        <h1>Your shopping cart</h1>

        <section className="shipping-progress">
          <p>
            {remaining > 0
              ? `Add ${formatPrice(remaining)} for complimentary shipping.`
              : "You qualify for complimentary shipping."}
          </p>
          <div><span style={{ width: `${progress}%` }} /></div>
        </section>

        <div className="cart-layout">
          <section className="cart-items" aria-label="Cart items">
            {detailedItems.map((item) => (
              <article
                className="cart-item"
                key={`${item.id}-${item.size}-${item.color}`}
              >
                <Link to={`/shopDetails/${item.product.id}`} className="cart-item__image">
                  <img src={item.product.image} alt={item.product.name} />
                </Link>
                <div className="cart-item__details">
                  <Link to={`/shopDetails/${item.product.id}`}>
                    <strong>{item.product.name}</strong>
                  </Link>
                  <span>{item.color}</span>
                  <span>Size: {item.size}</span>
                  <div className="cart-item__bottom">
                    <div className="cart-quantity">
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.size, item.color, item.quantity - 1)
                        }
                        aria-label="Decrease quantity"
                      >
                        <FiMinus />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.size, item.color, item.quantity + 1)
                        }
                        aria-label="Increase quantity"
                      >
                        <FiPlus />
                      </button>
                    </div>
                    <button
                      className="cart-remove"
                      onClick={() => setPendingRemove({ id: item.id, size: item.size, color: item.color, name: item.product.name })}
                    >
                      <FiTrash2 /> Remove
                    </button>
                  </div>
                </div>
                <strong className="cart-item__price">
                  {formatPrice(Number(item.product.price) * item.quantity)}
                </strong>
              </article>
            ))}
            <Link className="continue-shopping" to="/shop">Continue shopping</Link>
          </section>

          <aside className="cart-summary">
            <h2>Summary</h2>
            <dl>
              <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
              {discount > 0 && <div><dt>Discount</dt><dd>−{formatPrice(discount)}</dd></div>}
              <div><dt>Shipping</dt><dd>{shipping ? formatPrice(shipping) : "Complimentary"}</dd></div>
              <div><dt>Sales tax</dt><dd>Calculated at checkout</dd></div>
              <div className="cart-summary__total">
                <dt>Total</dt>
                <dd>{formatPrice(discountedSubtotal + shipping)}</dd>
              </div>
            </dl>
            <p>Taxes and duties will be confirmed before payment.</p>
            <Link className="button" to="/checkout">Go to checkout</Link>
            <details>
              <summary>Promo code or gift card</summary>
              <form onSubmit={handlePromo}>
                <input aria-label="Promo code" placeholder="Enter code" value={promoCode} onChange={(event) => setPromoCode(event.target.value)} />
                <button type="submit">Apply</button>
              </form>
              {promoMessage && <p className={promoApplied ? "promo-success" : "promo-error"} role="status">{promoMessage}</p>}
            </details>
            <details>
              <summary>Gift box</summary>
              <label className="gift-option">
                <input type="checkbox" checked={giftWrap} onChange={(event) => setGiftWrap(event.target.checked)} />
                <span>Make it a gift and omit prices from the packing slip.</span>
              </label>
            </details>
          </aside>
        </div>
      </div>
      {pendingRemove && (
        <div className="cart-confirm-overlay" role="presentation">
          <section className="cart-confirm" role="dialog" aria-modal="true" aria-labelledby="remove-title">
            <h2 id="remove-title">Remove this item?</h2>
            <p>{pendingRemove.name}</p>
            <div>
              <button className="button button--outline" onClick={() => setPendingRemove(null)}>Keep item</button>
              <button
                className="button"
                onClick={() => {
                  removeFromCart(pendingRemove.id, pendingRemove.size, pendingRemove.color);
                  setPendingRemove(null);
                }}
              >
                Remove item
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default CartPage;
